import {
  forwardRef,
  HTMLAttributes,
  memo,
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { message, Tabs } from 'antd';
import LoadMore, { LoadMoreProps } from 'components/LoadMore';
import useIM from 'hooks/business/im/useIM';
import Empty from 'components/Empty';
// eslint-disable-next-line camelcase
import { unstable_batchedUpdates } from 'react-dom';
import useCancelToken from 'hooks/useCancelToken';
import TradeComponent from './TradeComponent';
import TradeComponentDetail from './TradeComponentDetail';
import TradeSearchBar, { searchLoadRenderer } from './TradeSearchBar';
import useTrade, { defaultPage } from './useTrade';
import { TradeListContext } from './TradeContext';
import { uniqBy } from 'lodash-es';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { tradeDetailFirstTipCache } from 'services/cache';
import { useTradeOverThreeMonthEnable } from 'hooks/business/useOrgConfig';
import { getOrderStatusLabel } from 'services/i18n/enum/order';
import { useMemoizedFn } from 'ahooks';
import wait from 'utils/wait';

export interface TradeProps extends HTMLAttributes<HTMLDivElement> {
  conversation: Conversation.Conversation;
  showFilter?: boolean;
  drawerWidth?: number;
  relatedTrade?: Conversation.Trade.TradeWithLogistics;
  relatedRender?: (trade: ReactNode) => ReactNode;
}

function Trade(props: TradeProps, ref: Ref<any>) {
  const {
    conversation,
    showFilter = true,
    drawerWidth,
    relatedTrade,
    relatedRender = (trade) => trade,
    ...otherProps
  } = props;

  const { channel, tunnelAccountId, packId, storeId, buyerId } = conversation;

  const TRADE_OVER_THREE_MONTH = useTradeOverThreeMonthEnable();

  const useTradeResult = useTrade(storeId);
  const listRef = useRef<HTMLUListElement>(null);

  const {
    page,
    hasMore,
    list: listWithoutRelated,
    setPage,
    setHasMore,
    setList,
    fetchList,
    loading,
    lastFetchParamsRef,
  } = useTradeResult;

  const list = useMemo(() => {
    if (!relatedTrade) {
      return listWithoutRelated || [];
    }

    if (relatedTrade) {
      const relatedTradeFromList = listWithoutRelated?.find(
        ({ internalTradeId }) => internalTradeId === relatedTrade.internalTradeId,
      );

      return uniqBy(
        [
          relatedTradeFromList?.logisticsList ||
          relatedTradeFromList?.lazadaLogistics ||
          relatedTradeFromList?.thirdLogisticsList
            ? relatedTradeFromList
            : relatedTrade,
          ...listWithoutRelated,
        ],
        'internalTradeId',
      );
    }
  }, [relatedTrade, listWithoutRelated]);

  const { pageNum } = page;
  const pageSize = 4;

  const [tradeStatus, setTradeStatus] = useState<
    Omit<Conversation.Trade.Status, 'IN_CANCELING'> | 'ALL'
  >('ALL');

  const { t } = useTranslate();

  const [curTrade, setCurTrade] = useState<Conversation.Trade.TradeWithLogistics>();

  const [searchParams, setSearchParams] = useState({});
  const { newCancelToken, isCancel } = useCancelToken();

  useImperativeHandle(ref, () => ({
    openDetail: (trade: Conversation.Trade.TradeWithLogistics) => {
      setShowDetail(trade.internalTradeId);
      tradeDetailFirstTipCache.set('1');
      setCurTrade(trade);
    },
  }));

  const { on } = useIM();
  useEffect(() => {
    // 这个事件来自订单同步，如果有新的订单，需要刷新本地订单列表
    return on('sys:message:sync_tiktok_trade_uid', (msg) => {
      // 如果当前会话不是来自tts可以略过该刷新
      if (!isTiKTokUpperCase(conversation?.channel)) {
        return;
      }
      if (msg?.content?.success && lastFetchParamsRef.current) {
        fetchList(lastFetchParamsRef.current?.params, lastFetchParamsRef.current.platform);
      }
    });
  }, [conversation?.channel, fetchList, lastFetchParamsRef, on]);

  const updateTrade = useCallback(
    (trade: Conversation.Trade.TradeWithLogistics) => {
      setList((curList = []) => {
        const targetIndex = curList.findIndex(({ internalTradeId }) => {
          return internalTradeId === trade.internalTradeId;
        });

        if (targetIndex === -1) {
          return curList;
        }

        return [...curList.slice(0, targetIndex), trade, ...curList.slice(targetIndex + 1)];
      });
    },
    [setList],
  );

  const [showDetail, setShowDetail] = useState('');

  const onDetailClose = useCallback(() => {
    setShowDetail('');
    setCurTrade(undefined);
  }, []);

  // 订单状态 tab 列表
  const tradeStatusTabs = useMemo(() => {
    if (channel === 'MERCADO') {
      // Mercado 没有 未付款 状态
      return ['ALL', 'WAITING_DELIVERY', 'WAITING_SIGN', 'TRADE_SUCCESS', 'CANCEL', 'REFUND'];
    }
    return [
      'ALL',
      'WAITING_PAY',
      'WAITING_DELIVERY',
      'WAITING_SIGN',
      'TRADE_SUCCESS',
      'CANCEL',
      'REFUND',
    ];
  }, [channel]);

  const tradeListParams = {
    page_no: pageNum + 1,
    page_size: pageSize,
    internal_buyer_id: buyerId,
    internal_store_id: storeId,
    tunnelAccountId,
    ...(tradeStatus === 'ALL'
      ? {}
      : {
          trade_status: tradeStatus,
        }),
    external_trade_ids: packId ? packId : undefined,
    ...searchParams,
  };

  const refsCurrent = {
    tradeListParams,
    list,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  const [searchKey, setSearchKey] = useState(0);

  const onScrollTo = useMemoizedFn((id: string) => {
    const targetIndex = (list || []).findIndex(({ externalTradeId }) => externalTradeId === id);
    if (targetIndex > -1) {
      listRef.current?.children[targetIndex]?.scrollIntoView?.({ behavior: 'smooth' });
      return Promise.resolve();
    }
    if (tradeStatus !== 'ALL' || Object.values(searchParams).filter((_) => _).length) {
      setTradeStatus('ALL');
      setSearchParams({
        external_trade_ids: id,
      });
      resetState();
      setSearchKey((pre) => pre + 1);
      return Promise.resolve();
    }
    const fetch = (): Promise<any> => {
      return fetchList(refs.current.tradeListParams, channel, newCancelToken).then(() => {
        return wait(200).then(() => {
          const targetIndex = (refs.current.list || []).findIndex(
            ({ externalTradeId }) => externalTradeId === id,
          );
          if (targetIndex > -1) {
            listRef.current?.children[targetIndex]?.scrollIntoView?.({ behavior: 'smooth' });
          } else {
            return fetch();
          }
        });
      });
    };
    return fetch().catch(() => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    });
  });

  if (conversation === undefined) {
    return null;
  }

  const resetState = () => {
    unstable_batchedUpdates(() => {
      setPage({ ...defaultPage, pageSize });
      setHasMore(true);
      setList([]);
    });
  };

  const handleSearch = (params: Record<any, any>) => {
    resetState();
    setSearchParams(params);
  };

  const searchMode = Object.values(searchParams).filter((each) => Boolean(each))?.length > 0;
  const loadRenderer = searchMode
    ? {
        loadRenderer: searchLoadRenderer(t('scene.chatWorkspace.orderTab.search.empty.matchLimit')),
      }
    : channel === 'SHOPEE' && !TRADE_OVER_THREE_MONTH
    ? {
        loadRenderer: ((nodes, hasMore, hasError) => {
          const { loadMore, loadError } = nodes;
          if (hasError) {
            return loadError;
          }
          if (hasMore) {
            return loadMore;
          }
          return (
            <div
              className="flex items-center justify-center px-8 py-4 text-[12px] text-[var(--fett-text-tertiary)] text-center"
              {...props}
            >
              {t('scene.chatWorkspace.orderTab.notice.orderHistoryLimit')}
            </div>
          );
        }) as LoadMoreProps['loadRenderer'],
      }
    : {};

  const showSearchBar = showFilter && channel !== 'MERCADO';

  return (
    <TradeListContext.Provider
      value={{
        ...useTradeResult,
        conversation,
      }}
    >
      <Container {...otherProps}>
        {showFilter && (
          <Tabs
            size="small"
            activeKey={tradeStatus}
            onChange={(key) => {
              setTradeStatus(key as Conversation.Trade.Status | 'ALL');
              setSearchParams({
                external_trade_ids: packId ? packId : undefined,
                itemName: undefined,
                spuId: undefined,
                skuId: undefined,
                sellerSku: undefined,
              });
              resetState();
            }}
          >
            {tradeStatusTabs.map((status) => (
              <Tabs.TabPane key={status} tab={getOrderStatusLabel(t, status)} />
            ))}
          </Tabs>
        )}

        {showSearchBar && (
          <TradeSearchBar
            key={searchKey}
            loading={loading}
            value={searchParams}
            handleSearch={handleSearch}
          />
        )}

        {!hasMore && list?.length === 0 ? (
          <Empty
            style={{ marginTop: '41px' }}
            description={
              searchMode ? (
                <>
                  <EmptyDescription>
                    {t('scene.chatWorkspace.orderTab.search.empty.noResult')}
                  </EmptyDescription>
                  <EmptyDescription>
                    {t('scene.chatWorkspace.orderTab.search.empty.matchLimit')}
                  </EmptyDescription>
                </>
              ) : (
                <EmptyDescription>
                  {channel === 'SHOPEE' && !TRADE_OVER_THREE_MONTH
                    ? t('scene.chatWorkspace.orderTab.order.unavailableDueToShopeePolicy')
                    : t('scene.chatWorkspace.orderTab.empty.noOrders', {
                        status: tradeStatus === 'ALL' ? '' : getOrderStatusLabel(t, tradeStatus),
                      })}
                </EmptyDescription>
              )
            }
          />
        ) : (
          <LoadMore
            hasMore={hasMore}
            onLoadMore={() => {
              return fetchList(tradeListParams, channel, newCancelToken).catch((err) => {
                if (isCancel(err)) return;
                message.error(t('scene.chatWorkspace.orderTab.error.fetchFailed'));

                return Promise.reject(err);
              });
            }}
            {...loadRenderer}
          >
            {(loadEle) => {
              return (
                <List ref={listRef}>
                  {list?.map((trade) => {
                    const { internalTradeId: id } = trade;
                    const showBreath = !tradeDetailFirstTipCache.get();

                    const tradeNode = (
                      <TradeComponent
                        key={id}
                        trade={trade}
                        conversation={conversation}
                        onUpdate={updateTrade}
                        setCurTrade={setCurTrade}
                        showDetail={showDetail}
                        setShowDetail={setShowDetail}
                        showBreath={showBreath}
                        lazyScrollContainer={listRef.current}
                        onScrollTo={onScrollTo}
                      />
                    );

                    if (id === relatedTrade?.internalTradeId) {
                      return <li key={id}>{relatedRender(tradeNode)}</li>;
                    }

                    return <li key={id}>{tradeNode}</li>;
                  })}
                  <li>{loadEle}</li>
                </List>
              );
            }}
          </LoadMore>
        )}
        {curTrade ? (
          <TradeComponentDetail
            trade={curTrade}
            conversation={conversation}
            width={drawerWidth}
            onUpdate={updateTrade}
            onDetailClose={onDetailClose}
          />
        ) : null}
      </Container>
    </TradeListContext.Provider>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  .${({ theme }) => theme.getAntdPrefixedClassName('tabs')} {
    &-nav::before {
      display: none;
    }
    &-ink-bar {
      display: none;
    }
  }
  > ul {
    flex: 1 0 0;
  }
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  border-top: solid 1px var(--fett-border-default);
  overflow: auto;
  background: var(--fett-bg-subtle);
  > li {
    padding-bottom: 8px;
  }
`;

const EmptyDescription = styled.div`
  text-align: center;
  color: var(--fett-text-tertiary);
`;

export default memo(forwardRef(Trade));
